// #86 - creating an agency generates its portal link (domain) from the name,
// then presents it in a modal: copy it to share, or edit the domain on the spot
// if the generated one is not wanted. The form itself has no domain field.
import { useState } from "react";
import { Create, useDataProvider, useGetOne, useNotify, useRedirect } from "react-admin";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import AgencyFields from "./Fields";
import { copyText, linkFor } from "./purl";

const AgencyLinkDialog = ({ agency, onClose }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  // The create response mirrors the submitted form (data-provider convention),
  // so the generated domain is only on the stored record; fetch it.
  const { data: stored } = useGetOne("agency", { id: agency.id });
  const [domain, setDomain] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  if (stored?.domain && !domain && !editing) {
    setDomain(stored.domain);
    setDraft(stored.domain);
  }

  const copy = async () => {
    const ok = await copyText(linkFor(domain));
    notify(ok ? "Link copied" : "Could not copy the link", {
      type: ok ? "success" : "error",
    });
  };

  const saveDomain = async () => {
    setBusy(true);
    try {
      await dataProvider.update("agency", {
        id: agency.id,
        data: { domain: draft },
        previousData: agency,
      });
      // The backend normalizes (lowercase, spaces to dashes); mirror that here.
      const stored = draft.toLowerCase().replace(/ /g, "-");
      setDomain(stored);
      setDraft(stored);
      setEditing(false);
      notify("Domain updated", { type: "success" });
    } catch (e) {
      notify("Could not update the domain", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm">
      <DialogTitle>{agency.name} is ready</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          This is the agency's portal link. Share it with the agency; their
          agents sign up and submit through it.
        </Typography>
        {!editing ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <Typography
              variant="h6"
              component="a"
              href={linkFor(domain)}
              target="_blank"
              rel="noreferrer"
              sx={{ wordBreak: "break-all" }}
            >
              {linkFor(domain)}
            </Typography>
          </Box>
        ) : (
          <TextField
            fullWidth
            sx={{ mt: 1 }}
            label="Domain"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            helperText="The link becomes https:// plus this domain."
          />
        )}
      </DialogContent>
      <DialogActions>
        {!editing ? (
          <>
            <Button startIcon={<EditIcon />} onClick={() => setEditing(true)}>
              Edit domain
            </Button>
            <Button startIcon={<ContentCopyIcon />} onClick={copy}>
              Copy link
            </Button>
            <Button variant="contained" onClick={onClose}>
              Done
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => { setDraft(domain); setEditing(false); }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={busy || !draft.trim()}
              onClick={saveDomain}
            >
              {busy ? "Saving..." : "Save domain"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export const AgencyCreate = () => {
  const [created, setCreated] = useState(null);
  const redirect = useRedirect();
  return (
    <>
      <Create mutationOptions={{ onSuccess: (data) => setCreated(data) }}>
        <AgencyFields />
      </Create>
      {created ? (
        <AgencyLinkDialog
          agency={created}
          onClose={() => {
            setCreated(null);
            redirect("list", "agency");
          }}
        />
      ) : null}
    </>
  );
};

export default AgencyCreate;
